/**
 * Freeform-canvas text fonts: deterministic, bundled families shared by web and mobile so
 * canvas text renders identically (same glyphs/metrics) on both platforms instead of falling
 * back to whatever the OS/browser happens to use for a generic family name.
 *
 * Each of the 6 underlying .ttf files is bundled natively on mobile
 * (apps/b2b-mobile/src/assets/fonts/) and self-hosted via @font-face on web
 * (apps/b2b-web/src/styles/globals.css), using the SAME file bytes on both platforms. Their
 * PostScript names (verified from each file's `name` table) are used as-is for both CSS
 * `font-family` and React Native `fontFamily` — no separate web/native mapping needed.
 */
export const AVAILABLE_FONTS = [
  {
    value: "System",
    label: "System (Default)",
    description: "KumbhSans — deterministic on web and mobile",
  },
  {
    value: "sans-serif",
    label: "Sans Serif",
    description: "KumbhSans — deterministic on web and mobile",
  },
  {
    value: "serif",
    label: "Serif",
    description: "Gelasio — deterministic on web and mobile",
  },
  {
    value: "monospace",
    label: "Monospace",
    description: "Courier Prime — deterministic on web and mobile",
  },
] as const;

export type AvailableFont = (typeof AVAILABLE_FONTS)[number]["value"];

export type FontBucket = "system" | "sans-serif" | "serif" | "monospace";

const BUCKET_BY_FONT: Record<string, FontBucket> = {
  system: "system",
  "sans-serif": "sans-serif",
  serif: "serif",
  monospace: "monospace",
  // legacy named faces the old editor / seed data stored
  kumbhsans: "system",
  "kumbh sans": "system",
  inter: "sans-serif",
  arial: "sans-serif",
  "arial black": "sans-serif",
  helvetica: "sans-serif",
  verdana: "sans-serif",
  impact: "sans-serif",
  "comic sans ms": "sans-serif",
  georgia: "serif",
  "times new roman": "serif",
  "brush script mt": "serif",
  "courier new": "monospace",
  courier: "monospace",
  consolas: "monospace",
};

/** Collapse any stored `fontFamily` string onto a cross-platform bucket. */
export function normalizeFontBucket(value?: string | null): FontBucket {
  if (!value) return "system";
  const key = value.split(",")[0]?.trim().replace(/['"]/g, "").toLowerCase() ?? "";
  return BUCKET_BY_FONT[key] ?? "system";
}

/**
 * The deterministic font family for each bucket, by weight. PostScript names, identical on
 * web (self-hosted @font-face) and mobile (bundled native font) — usable directly as a
 * `fontFamily`/CSS `font-family` value on either platform.
 */
const CANVAS_FONT_FILES: Record<FontBucket, { normal: string; bold: string }> = {
  system: { normal: "KumbhSans-Regular", bold: "KumbhSans-Bold" },
  "sans-serif": { normal: "KumbhSans-Regular", bold: "KumbhSans-Bold" },
  serif: { normal: "Gelasio-Regular", bold: "Gelasio-Bold" },
  monospace: { normal: "CourierPrime-Regular", bold: "CourierPrime-Bold" },
};

/** The canvas system only ever authors "normal" or "bold" (see TextElementToolbar.tsx). */
function isBoldFontWeight(fontWeight?: string | null): boolean {
  if (!fontWeight) return false;
  if (fontWeight === "bold") return true;
  const numeric = Number(fontWeight);
  return !Number.isNaN(numeric) && numeric >= 600;
}

/**
 * Resolve any stored `fontFamily`/`fontWeight` pair to the deterministic canvas font file —
 * the single source of truth every text-bearing canvas renderer (web and mobile) should call
 * instead of deciding a platform font independently.
 */
export function resolveCanvasFontFamily(
  fontFamily?: string | null,
  fontWeight?: string | null
): string {
  const bucket = normalizeFontBucket(fontFamily);
  const files = CANVAS_FONT_FILES[bucket];
  return isBoldFontWeight(fontWeight) ? files.bold : files.normal;
}

/** Canonical sans font for renderers with no per-element font/weight selection. */
export const CANVAS_DEFAULT_FONT_FAMILY = CANVAS_FONT_FILES.system.normal;

/** Bold counterpart of CANVAS_DEFAULT_FONT_FAMILY — use this file directly, never layer `fontWeight` on top of the Regular file. */
export const CANVAS_DEFAULT_FONT_FAMILY_BOLD = CANVAS_FONT_FILES.system.bold;
